import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'

import { PaymentRow, LedgerPeriod, PaymentQuality, LeaseLedger } from '../../../data/interfaces/lease-ledger.interface'

//* Una barra de la mini-gráfica: días de retraso de una letra cubierta, o días vencida de una abierta.
export interface SparkBar {
  n: number
  days: number
  open: boolean
  /** Alto en % del carril, ya normalizado contra la barra más alta. */
  height: number
}

@Component({
  selector: 'app-ledger-strip',
  templateUrl: './ledger-strip.component.html',
  styleUrls: ['./ledger-strip.component.scss'],
})
export class LedgerStripComponent implements OnChanges {
  @Input() totals!: LeaseLedger['totals']
  @Input() quality: PaymentQuality | null = null
  @Input() periods: LedgerPeriod[] = []
  @Input() unapplied: PaymentRow[] = []
  @Input() moraPaused = false

  /*
   * Sin acciones: la franja solo informa.
   *
   *! El libro mayor que se abre desde Cartera dice en su propio aviso «vista de solo lectura», y aun
   *! así la casilla de acción pintaba «Aplicar» — un botón que ahí no está conectado a nada. El
   *! permiso no alcanza para esconderlo: Contabilidad no lo tiene, pero Admin y Operaciones sí, y
   *! son quienes más van a abrir esa pantalla. Va como entrada y no como CSS porque un botón
   *! escondido con estilo sigue recibiendo el foco con el teclado.
   */
  @Input() readOnly = false
  /** Mientras el libro viaja, la franja se pinta como esqueleto con su misma geometría. */
  @Input() loading = false

  //* Alturas de la silueta de la mini-gráfica del esqueleto: una forma de gráfica, no datos de nadie.
  readonly sparkSkeleton = [40, 70, 30, 85, 50, 65, 35, 55]
  @Output() apply = new EventEmitter<void>()
  @Output() residual = new EventEmitter<void>()

  // ── Contexto operativo derivado del ledger, sin request extra ──
  //
  // Se calcula en `ngOnChanges` y no en getters: el template los lee dentro de `*ngFor` y de
  // interpolaciones que corren en cada ciclo de detección de cambios; `sparkBars` construye objetos
  // nuevos, así que como getter obligaría a Angular a rehacer todas las barras en cada ciclo.
  unappliedAmount = 0
  oldestOverdueDays = 0
  moraPaidTotal = 0
  oldestUnappliedPaidOn: string | null = null
  hasTwins = false
  daysToNextDue: number | null = null
  residualOnly = false
  sparkBars: SparkBar[] = []

  ngOnChanges(): void {
    //* Sin libro todavía no hay nada que derivar: el template pinta el esqueleto.
    if (this.loading || !this.totals) return
    const hoy = Date.now()
    const periods = this.periods ?? []
    const unapplied = this.unapplied ?? []

    this.unappliedAmount = unapplied.reduce((s, p) => s + (p.pendingToApply || 0), 0)
    this.oldestUnappliedPaidOn = unapplied[0]?.paidOn ?? null
    this.hasTwins = unapplied.some((p) => (p.twins?.length ?? 0) > 0)
    this.moraPaidTotal = periods.reduce((s, p) => s + (p.mora?.paid || 0), 0)

    //* Los días de atraso se cuentan del CALENDARIO y no de `mora.days`: con el interruptor de mora
    //* apagado ese campo vale 0 en toda letra y el sub-renglón «la más antigua lleva N días»
    //* desaparecía aunque el atraso fuera real. El atraso es un hecho de la fecha; cobrarlo por él
    //* es otra decisión, y son independientes.
    const vencidas = periods.filter((p) => p.pending > 0 && new Date(p.date).getTime() < hoy)
    this.oldestOverdueDays = vencidas.reduce((m, p) => Math.max(m, this.daysSince(p.date, hoy)), 0)

    const proxima = this.totals?.nextDue?.date
    this.daysToNextDue = proxima ? Math.round((new Date(proxima).getTime() - hoy) / 86400000) : null

    //* Sin letras elegibles y con dinero disponible: el sobrante solo puede ir al residual (F20). Elegible por saldo,
    //* como el API: una anticipada con su accesorio por vencer todavía recibe dinero (C5).
    this.residualOnly = this.unappliedAmount > 0 && !periods.some((p) => p.pending > 0)

    this.sparkBars = this.buildSpark(periods, hoy)
  }

  /** Una barra por letra con retraso medible: cubierta (azul/gris) o abierta y vencida (roja). */
  private buildSpark(periods: LedgerPeriod[], hoy: number): SparkBar[] {
    const cubiertas = periods.filter((p) => p.paidAt).map((p) => ({ n: p.n, days: p.delayDays || 0, open: false }))
    const abiertas = periods.filter((p) => !p.paidAt && p.pending > 0 && new Date(p.date).getTime() < hoy).map((p) => ({ n: p.n, days: this.daysSince(p.date, hoy), open: true }))
    const barras = [...cubiertas, ...abiertas].sort((a, b) => a.n - b.n)
    const max = Math.max(1, ...barras.map((b) => b.days))
    return barras.map((b) => ({ ...b, height: Math.max(6, Math.round((b.days / max) * 100)) }))
  }

  private daysSince(fecha: string, hoy: number): number {
    return Math.max(0, Math.floor((hoy - new Date(fecha).getTime()) / 86400000))
  }
}
